import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  Snackbar
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Email,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Security as SecurityIcon,
  WarningAmber
} from '@mui/icons-material';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService, usuarioService, nivelAcessoService } from '../../services/api';
import axios, { AxiosError } from 'axios';
import './Login.css';

// =====================================================
// TIPOS E INTERFACES
// =====================================================

interface LoginFormData {
  email: string;
  senha: string;
}

interface LoginFormErrors {
  email?: string;
  senha?: string;
  geral?: string;
}

// =====================================================
// FUNÇÕES DE VALIDAÇÃO
// =====================================================

const validarEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const validarCampos = (formData: LoginFormData): LoginFormErrors => {
  const erros: LoginFormErrors = {};
  
  if (!formData.email) {
    erros.email = 'Email é obrigatório';
  } else if (!validarEmail(formData.email)) {
    erros.email = 'Email inválido';
  }
  
  if (!formData.senha) {
    erros.senha = 'Senha é obrigatória';
  }
  // Removida a validação de comprimento mínimo para não bloquear o login
  // A validação de segurança deve ser feita no backend
  
  return erros;
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, updateUser, setUserPermissions } = useAuth(); // Adicionar setUserPermissions
  
  // =====================================================
  // ESTADOS
  // =====================================================
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    senha: ''
  });
  
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [show2FAModal, setShow2FAModal] = useState<boolean>(false);
  const [codigo2FA, setCodigo2FA] = useState<string>('');
  const [codigo2FAGerado, setCodigo2FAGerado] = useState<string | null>(null);
  const [usuario, setUsuario] = useState<any>(null);
  const [enviado, setEnviado] = useState<boolean>(false);
  const [aguardando2FA, setAguardando2FA] = useState<boolean>(false);
  const [inactiveOpen, setInactiveOpen] = useState<boolean>(false);

  // Recuperação de senha
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success'|'error'|'warning'|'info'}>({ open: false, message: '', severity: 'info' });
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // 2FA - estado de envio por e-mail
  const [twoFAEmailLoading, setTwoFAEmailLoading] = useState(false);
  const [twoFAEmailCooldown, setTwoFAEmailCooldown] = useState<number>(0);
  const twoFAIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Helpers de mensagens amigáveis
  const mapResetErrorMessage = (msg?: string) => {
    if (!msg) return 'Falha ao redefinir a senha. Tente novamente.';
    const m = msg.toLowerCase();
    if (m.includes('expirado')) return 'Seu link de redefinição expirou. Clique em “Esqueceu sua senha?” para gerar um novo link.';
    if (m.includes('inválido') || m.includes('invalido')) return 'Link inválido. Clique em “Esqueceu sua senha?” para gerar um novo link.';
    if (m.includes('senha fraca') || m.includes('fraca')) return 'Senha fraca. Use 8+ caracteres, com letra, número e caractere especial.';
    if (m.includes('não encontrado') || m.includes('nao encontrado')) return 'Não encontramos uma conta para este link. Gere um novo link em “Esqueceu sua senha?”.';
    return msg;
  };

  // =====================================================
  // EFEITOS
  // =====================================================
  useEffect(() => {
    // Se já estiver autenticado, redireciona para o dashboard
    if (isAuthenticated && !aguardando2FA) {
      const from = location.state?.from?.pathname || '/app/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location, aguardando2FA]);

  // Detectar token de reset na URL (/reset-senha?token=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setResetOpen(true);
    } else if (location.pathname === '/reset-senha') {
      // Se acessar /reset-senha sem token, manter formulário aberto para informar erro ao enviar
      setResetOpen(true);
    }
  }, [location.search, location.pathname]);

  // Focar automaticamente no campo 2FA quando o modal abrir
  useEffect(() => {
    if (show2FAModal) {
      setTimeout(() => codigo2FARef.current?.focus(), 50);
    }
  }, [show2FAModal]);

  // =====================================================
  // HANDLERS
  // =====================================================
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name as keyof LoginFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Função de validação que estava faltando
  const validarFormulario = (): boolean => {
    const erros = validarCampos(formData);
    setErrors(erros);
    return Object.keys(erros).length === 0;
  };

  // Abrir modal de esqueci minha senha (pré-preenche o e-mail digitado)
  const handleOpenForgot = () => {
    setForgotEmail(formData.email || '');
    setForgotOpen(true);
  };

  const handleEnviarForgot = async () => {
    if (!forgotEmail || !validarEmail(forgotEmail)) {
      setSnackbar({ open: true, message: 'Informe um e-mail válido.', severity: 'warning' });
      return;
    }
    try {
      const resp = await authService.forgotPassword(forgotEmail);
      const ok = (resp && (resp.success === true)) || true; // backend retorna sempre genérico
      setSnackbar({ open: true, message: 'Se o e-mail existir, enviaremos um link para redefinir sua senha.', severity: ok ? 'success' : 'error' });
      setForgotOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Falha ao processar. Tente novamente em instantes.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const senhaForte = (senha: string) => /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(senha);

  const handleEnviarReset = async () => {
    if (!resetToken) {
      setSnackbar({ open: true, message: 'Token ausente ou inválido. Solicite novamente.', severity: 'error' });
      return;
    }
    if (!senhaForte(novaSenha)) {
      setSnackbar({ open: true, message: 'Senha fraca. Use 8+ caracteres com letra, número e caractere especial.', severity: 'warning' });
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setSnackbar({ open: true, message: 'As senhas não coincidem.', severity: 'warning' });
      return;
    }
    try {
      setResetLoading(true);
      const resp = await authService.resetPassword(resetToken, novaSenha);
      if (resp?.success) {
        setSnackbar({ open: true, message: 'Senha redefinida com sucesso. Faça login com a nova senha.', severity: 'success' });
        setResetOpen(false);
        setNovaSenha('');
        setConfirmarSenha('');
        // Limpar token da URL
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());
      } else {
        const msg = mapResetErrorMessage(resp?.message);
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const raw = err?.response?.data?.message || err?.message;
      if (status === 400 && raw) {
        const msg = mapResetErrorMessage(raw);
        setSnackbar({ open: true, message: msg, severity: 'error' });
      } else if (status === 500) {
        setSnackbar({ open: true, message: 'Erro no servidor ao redefinir a senha. Tente novamente em instantes.', severity: 'error' });
      } else {
        const msg = mapResetErrorMessage(raw);
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validarFormulario()) {
      return;
    }
    
    // Checar status de usuário antes de prosseguir com login
    try {
      const statusResp = await authService.checkStatus(formData.email);
      if (statusResp?.data && statusResp.data.ativo === false) {
        setErrors({ geral: '' });
        setInactiveOpen(true);
        return;
      }
    } catch (err) {
      // Se a checagem falhar, seguimos o fluxo normal de login
    }
    
    setLoading(true);
    setErrors({ geral: '' });
    
    try {
      console.log('🚀 [LOGIN] Fazendo requisição para o backend...');
      const response = await authService.login(formData.email, formData.senha);
      console.log('🚀 [LOGIN] Resposta do backend:', response);
      
      if (response.success) {
        // Verificar se existe data e usuario na resposta de forma segura
        const responseAny = response as any;
        const userData = response.data?.usuario || responseAny.usuario || response.data;
        
        // ✅ CORREÇÃO: Usar o campo correto do backend
        if (userData && userData.login_duas_etapas_whatsapp) {
          console.log('🔐 [LOGIN] 2FA necessário para usuário:', userData.id);
          
          // Gerar código 2FA
          const codigo = gerarCodigo2FA();
          console.log('🔐 [LOGIN] Código 2FA gerado:', codigo);
          
          // Enviar código via WhatsApp
          try {
            await usuarioService.enviarCodigo2FA(userData.id, codigo);
            console.log('📱 [LOGIN] Código 2FA enviado via WhatsApp');
            
            // Guardar o código gerado para reenvio por e-mail (não sobrescrever input)
            setCodigo2FAGerado(codigo);
            setUsuario(userData);
            setShow2FAModal(true);
          } catch (error) {
            console.error('❌ [LOGIN] Erro ao enviar código 2FA:', error);
            setErrors({ geral: 'Erro ao enviar código de verificação. Tente novamente.' });
          }
        } else {
          // Login direto sem 2FA
          const token = response.data?.token || responseAny.token;
          const user = userData || response.data || responseAny;
          
          // ✅ CORREÇÃO CRÍTICA: Garantir que user tenha nome e user_name
          const userCorrigido = {
            ...user,
            nome: user.userName || user.nome,
            user_name: user.userName || user.user_name
          };
          
          // Salvar no localStorage
          localStorage.setItem('token', token);
          localStorage.setItem('gestaopsi_user', JSON.stringify(userCorrigido));
          
          // Atualizar contexto diretamente
          updateUser(userCorrigido);
          
          // ✅ CORREÇÃO CRÍTICA: Carregar permissões após updateUser
          try {
            console.log('🔐 [LOGIN] Carregando permissões para usuário:', userCorrigido.id);
            const permissionsResponse = await nivelAcessoService.buscarPermissoesPorUsuario(userCorrigido.id);
            
            if (permissionsResponse && permissionsResponse.data) {
              console.log('✅ [LOGIN] Permissões carregadas:', permissionsResponse.data);
              localStorage.setItem('user_permissions', JSON.stringify(permissionsResponse.data));
              setUserPermissions(permissionsResponse.data); // Adicionar setUserPermissions
            } else {
              console.log('⚠️ [LOGIN] Nenhuma permissão encontrada para o usuário');
            }
          } catch (permError) {
            console.error('❌ [LOGIN] Erro ao carregar permissões:', permError);
          }
          
          const from = location.state?.from?.pathname || '/app/dashboard';
          navigate(from, { replace: true });
        }
      } else {
        setErrors({ geral: response.message || 'Credenciais inválidas' });
      }
    } catch (error) {
      console.error('❌ [LOGIN] Erro:', error);
      setErrors({ geral: 'Erro interno do servidor. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  // Função para verificar código 2FA (movida para dentro do componente)
  const handleVerificarCodigo2FA = async () => {
    // Checar status do usuário antes de verificar o código 2FA
    try {
      const statusResp = await authService.checkStatus(usuario?.email || '');
      if (statusResp?.data && statusResp.data.ativo === false) {
        setAguardando2FA(false);
        setShow2FAModal(false);
        setInactiveOpen(true);
        return;
      }
    } catch (_e) {}
    if (!codigo2FA || codigo2FA.length !== 4) {
      setErrors({ geral: 'Código deve ter 4 dígitos' });
      return;
    }
    
    setAguardando2FA(true);
    setErrors({ geral: '' });
    
    try {
      console.log('🔐 [2FA] Verificando código:', codigo2FA, 'para usuário:', usuario.id);
      const response = await usuarioService.verificarCodigo2FA(usuario.id, codigo2FA);
      console.log('🔐 [2FA] Resposta da verificação:', response);
      
      if (response.data && response.data.success) {
        console.log('✅ [2FA] Código verificado com sucesso!');
        
        // Fazer login novamente para obter o token
        const loginResponse = await authService.login(usuario.email, formData.senha);
        
        if (loginResponse.success && loginResponse.data) {
          const token = loginResponse.data.token;
          const userData = {
            id: loginResponse.data.id,
            email: loginResponse.data.email,
            userName: loginResponse.data.userName,
            nome: loginResponse.data.userName, // ✅ ADICIONAR nome
            user_name: loginResponse.data.userName, // ✅ ADICIONAR user_name
            foto: loginResponse.data.foto,
            role: loginResponse.data.role,
            login_duas_etapas_whatsapp: loginResponse.data.login_duas_etapas_whatsapp
          };
          
          // ✅ CORREÇÃO: Usar as chaves corretas do AuthContext
          localStorage.setItem('token', token);
          localStorage.setItem('gestaopsi_user', JSON.stringify(userData));
          
          // ✅ CORREÇÃO: Limpar dados temporários do 2FA
          localStorage.removeItem('2fa_ativo');
          localStorage.removeItem('2fa_usuario_id');
          localStorage.removeItem('2fa_codigo_gerado');
          localStorage.removeItem('2fa_email');
          localStorage.removeItem('2fa_senha');
          
          // ✅ CORREÇÃO: Atualizar contexto com dados corretos
          updateUser(userData);
          
          console.log('✅ [2FA] Usuário logado com sucesso:', userData);
          
          setShow2FAModal(false);
          const from = location.state?.from?.pathname || '/app/dashboard';
          navigate(from, { replace: true });
        } else {
          setErrors({ geral: 'Erro ao fazer login após verificação 2FA' });
        }
      } else {
        setErrors({ geral: response.data?.message || 'Código inválido' });
      }
    } catch (error) {
      console.error('❌ [2FA] Erro na verificação:', error);
      setErrors({ geral: 'Erro ao verificar código. Tente novamente.' });
    } finally {
      setAguardando2FA(false);
    }
  };

  const gerarCodigo2FA = (): string => {
    // ✅ CORREÇÃO: Gerar código de 4 dígitos ao invés de 6
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const codigo2FARef = React.useRef<HTMLInputElement | null>(null);

  const handleEnviar2FACodigoPorEmail = async () => {
    try {
      if (twoFAEmailCooldown > 0 || twoFAEmailLoading) return;
      setTwoFAEmailLoading(true);
      const emailDestino = formData.email;
      if (!emailDestino || !validarEmail(emailDestino)) {
        setSnackbar({ open: true, message: 'Informe seu e-mail de login na tela anterior para receber o código por e-mail.', severity: 'warning' });
        setTwoFAEmailLoading(false);
        return;
      }
      // Usar SEMPRE o mesmo código enviado por WhatsApp
      if (!codigo2FAGerado) {
        setSnackbar({ open: true, message: 'Código ainda não foi gerado. Clique em Entrar para gerar o código e tente novamente.', severity: 'warning' });
        setTwoFAEmailLoading(false);
        return;
      }
      const codigo = codigo2FAGerado;
      const resp = await authService.enviarCodigo2FAEmail(emailDestino, codigo);
      if (resp?.success) {
        setSnackbar({ open: true, message: 'Código enviado por e-mail. Verifique sua caixa de entrada e Spam.', severity: 'success' });
        // Iniciar cooldown de 60s
        setTwoFAEmailCooldown(60);
        if (twoFAIntervalRef.current) clearInterval(twoFAIntervalRef.current);
        twoFAIntervalRef.current = setInterval(() => {
          setTwoFAEmailCooldown((prev) => {
            if (prev <= 1) {
              if (twoFAIntervalRef.current) clearInterval(twoFAIntervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setSnackbar({ open: true, message: resp?.message || 'Não foi possível enviar o código por e-mail.', severity: 'error' });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Falha ao enviar o código por e-mail. Tente novamente.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setTwoFAEmailLoading(false);
    }
  };

  // =====================================================
  // RENDERIZAÇÃO
  // =====================================================
  
  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={60} />
      </div>
    );
  }
  
  if (usuario && aguardando2FA) {
    return <Navigate to="/2fa" replace />;
  }

  return (
    <div className="login-container">
      {/* Partículas flutuantes adicionais */}
      <div className="floating-particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>
      
      <Card className="login-card">
        <div className="login-logo">
          <img src="/logo-sistema-completa.png" alt="Trajetus" />
        </div>
        
        {errors.geral && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.geral}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="login-field">
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#c49968' }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          
          <div className="login-field">
            <TextField
              fullWidth
              label="Senha"
              name="senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.senha}
              onChange={handleInputChange}
              error={!!errors.senha}
              helperText={errors.senha}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#c49968' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          
          <Button
            type="submit"
            variant="contained"
            className="login-button"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
          </Button>
        </form>
        
        <div className="login-footer">
          <Typography variant="body2">
            Esqueceu sua senha?{' '}
            <a style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={handleOpenForgot}>Clique aqui</a>
          </Typography>
        </div>
      </Card>
      
      {/* Modal 2FA */}
      <Dialog 
        open={show2FAModal} 
        onClose={() => setShow2FAModal(false)}
        className="modal-2fa"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <span>Verificação em Duas Etapas</span>
            <img src="/logo-sistema-completa.png" alt="Trajetus" style={{ height: 58 }} />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Digite o código de 4 dígitos enviado para seu WhatsApp:
          </Typography>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleVerificarCodigo2FA(); }}>
            <TextField
              fullWidth
              label="Código 2FA"
              value={codigo2FA}
              inputRef={codigo2FARef}
              onChange={(e) => setCodigo2FA(e.target.value)}
              inputProps={{ maxLength: 4, inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'one-time-code' }}
              sx={{ mb: 2 }}
            />
          </Box>
          <Box sx={{ mt: 1, mb: 3, whiteSpace: 'nowrap' }}>
            <Typography variant="body2" component="span">
              Caso não tenha recebido, te enviamos por Email,
            </Typography>{' '}
            <a
              style={{ cursor: twoFAEmailCooldown > 0 || twoFAEmailLoading ? 'not-allowed' : 'pointer', textDecoration: 'underline', opacity: twoFAEmailCooldown > 0 || twoFAEmailLoading ? 0.6 : 1 }}
              onClick={twoFAEmailCooldown > 0 || twoFAEmailLoading ? undefined : handleEnviar2FACodigoPorEmail}
            >
              {twoFAEmailLoading ? 'ENVIANDO…' : twoFAEmailCooldown > 0 ? `REENVIAR EM ${twoFAEmailCooldown}s` : 'CLIQUE AQUI'}
            </a>.
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShow2FAModal(false)}>
              Cancelar
            </Button>
            <Button 
              variant="contained" 
              type="submit"
              onClick={() => handleVerificarCodigo2FA()}
              disabled={loading || codigo2FA.length !== 4}
              sx={{ 
                background: 'linear-gradient(135deg, #d4a574 0%, #c49968 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #c49968 0%, #b8906b 100%)'
                }
              }}
            >
              Verificar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Modal Esqueci minha senha */}
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)}>
        <DialogTitle>Esqueceu sua senha?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Vamos enviar um e-mail com um link para redefinir sua senha.
          </Typography>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEnviarForgot}>Enviar link</Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal Redefinição de senha por token */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src="/logo-sistema-completa.png" alt="Trajetus" style={{ height: 44 }} />
            <span>Redefinir Senha</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleEnviarReset(); }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Crie sua nova senha. Ela deve ter ao menos 8 caracteres, com letra, número e caractere especial.
            </Typography>
            <TextField
              fullWidth
              label="Nova senha"
              type={showResetPassword ? 'text' : 'password'}
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              sx={{ mb: 2 }}
              error={!!novaSenha && !senhaForte(novaSenha)}
              helperText={!!novaSenha && !senhaForte(novaSenha) ? 'Use 8+ caracteres, incluindo letra, número e caractere especial.' : ' '}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowResetPassword(p => !p)} edge="end" aria-label="mostrar/ocultar senha">
                      {showResetPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              label="Confirmar senha"
              type={showResetConfirm ? 'text' : 'password'}
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              sx={{ mb: 2 }}
              error={!!confirmarSenha && confirmarSenha !== novaSenha}
              helperText={!!confirmarSenha && confirmarSenha !== novaSenha ? 'As senhas não coincidem' : ' '}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowResetConfirm(p => !p)} edge="end" aria-label="mostrar/ocultar senha">
                      {showResetConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancelar</Button>
          <Button variant="contained" type="submit" onClick={handleEnviarReset} disabled={resetLoading}>
            {resetLoading ? 'Redefinindo…' : 'Redefinir'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      />

      {/* Popup de usuário inativo */}
      <Dialog open={inactiveOpen} onClose={() => { setInactiveOpen(false); }}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmber color="warning" />
            <Typography variant="h6">Atenção</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Seu usuário está inativo. Entre em contato com a Equipe Trajetus para maiores informações.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setInactiveOpen(false);
              setShow2FAModal(false);
              setCodigo2FA('');
              setCodigo2FAGerado(null);
              setUsuario(null);
              setAguardando2FA(false);
              setFormData({ email: '', senha: '' });
            }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Login;
